import {css, StyleSheet} from 'aphrodite';
import React, {useEffect, useReducer, useRef} from 'react';

import Modal from 'ui-library/modals/Modal';

import reducer from './importInstrumentDataModalReducer';
import {INITIAL_STATE} from './InstrumentDataConstants';
import InstrumentLoginPage from './InstrumentLoginPage';

export interface InstrumentLoginPageModalProps {
  connectionId: string;
  onRequestSubmit: (username: string) => void;
  onRequestClose: () => void;
  // Validate credentials via VALIDATE_CREDENTIALS (login-only probe) rather than BROWSE_FILES.
  // Used for non-file-browsing connections (e.g. Empower).
  useValidateCredentials?: boolean;
  // What logging in is for, shown above the form.
  helpText?: string;
}

export default function InstrumentLoginPageModal(props: InstrumentLoginPageModalProps): JSX.Element {
  const [state, dispatch] = useReducer(reducer, INITIAL_STATE);
  // A credential check can still be polling when the user closes the modal; its result must not
  // continue the action they cancelled.
  const isActiveRef = useRef(true);
  useEffect(() => {
    isActiveRef.current = true;
    return () => {
      isActiveRef.current = false;
    };
  }, []);

  const closeHandler = (): void => {
    isActiveRef.current = false;
    props.onRequestClose();
  };

  const loginSucceededHandler = (username: string): void => {
    if (isActiveRef.current) {
      props.onRequestSubmit(username);
    }
  };

  return (
    <Modal closeableOnEscapeKey={false} large={true} onRequestClose={closeHandler} title="Login">
      <p className={css(styles.helpText)}>{props.helpText ?? 'Login to generate and send your file.'}</p>
      <InstrumentLoginPage
        connectionId={props.connectionId}
        dispatch={dispatch}
        state={state}
        useValidateCredentials={props.useValidateCredentials}
        onLoginSucceeded={loginSucceededHandler}
      />
    </Modal>
  );
}

export const styles = StyleSheet.create({
  helpText: {
    'font-style': 'italic',
    padding: '10px 0 20px',
  },
});
